import type { Hex } from 'viem'
import { z } from 'zod'
import type { LowerHex } from '../../types'
import { addressPrimitiveSchema, lowerHexSchema } from '../../types/primitive'
import type { LogWithBlockNumber } from '../types'
import type { EtherscanClient } from './client'

type GetEventLogsByAddressFilteredByTopicsParameters = {
  client: EtherscanClient
  address: Hex
  topic0: LowerHex
  fromBlock?: number
  toBlock?: number
  headers?: Record<string, string>
}

const logSchema = z.object({
  address: addressPrimitiveSchema,
  blockNumber: z
    .string()
    .regex(/^0x[\da-f]*$/)
    .transform((x) => (x === '0x' ? 0 : Number(x))),
  data: lowerHexSchema,
  logIndex: z
    .string()
    .regex(/^0x[\da-f]*$/)
    .transform((x) => (x === '0x' ? 0 : Number(x))),
  topics: z
    .array(lowerHexSchema.nullable())
    .transform((x) => x.filter((v): v is LowerHex => v !== null)),
  transactionHash: lowerHexSchema,
})
const resultSchema = z.array(logSchema)

/** {@link https://docs.etherscan.io/api-endpoints/logs#get-event-logs-by-address-filtered-by-topics} */
export const getEventLogsByAddressFilteredByTopics = async ({
  client,
  address,
  topic0,
  fromBlock,
  toBlock,
  headers,
}: GetEventLogsByAddressFilteredByTopicsParameters): Promise<
  LogWithBlockNumber[]
> => {
  try {
    const result = await client.get({
      path: '',
      query: {
        module: 'logs',
        action: 'getLogs',
        address,
        topic0,
        ...(fromBlock === undefined ? {} : { fromBlock: fromBlock.toString() }),
        ...(toBlock === undefined ? {} : { toBlock: toBlock.toString() }),
      },
      resultSchema,
      headers,
    })
    return result
  } catch (error) {
    if (error instanceof Error && error.message.includes('No records found')) {
      return []
    }
    throw error
  }
}
